import React from 'react';
import {
  ColDef,
  ICellRendererParams,
  SortDirection,
  ColumnMenuTab,
  GetMainMenuItemsParams,
  MenuItemDef,
} from '@ag-grid-community/core';

import { ViewColumn } from 'common/types/viewColumn';
import { TableColumnFormat, UniqueValuesData, MAX_CUSTOM_SORT_UNIQUE_ROW_COUNT, FormatStyle } from '../types';
import { CustomSortConfig, Hierarchy, HierarchyColumnConfig, OrderConfig } from 'common/visualizations/vif';
import { ColumnAggregation } from 'common/visualizations/dataProviders/types';
import { SoQLType, isGeospatial } from 'common/types/soql';
import { getAgColFilterObject } from 'common/visualizations/helpers/AgGridHelpers';
import { getAgTableHeaderName, getAllowedColumnAggregationFunctions } from './TableColumnFormatting';
import {
  cellStyleFunction,
  headerClassFunction,
  tooltipValueGetterFunction,
  valueGetterFunction,
} from './ColDefHelpers';
import { ValueRenderer } from '../ValueRenderer';
import { GROUPABLE_DATA_TYPES } from '../Constants';
import getCustomSortMenuItems from './getCustomSortMenuItems';
import { isPivotFeatureFlagOn } from 'common/visualizations/helpers/PivotHelpers';
import get from 'lodash/get';
import { isCustomSortEnabled } from 'common/visualizations/helpers/VifSelectors';
import { ISortIcons } from './sortIconHelpers';

function getColumnSort(column: ViewColumn, orders: OrderConfig[]) {
  const orderIndex = orders.findIndex((o: OrderConfig) => column.fieldName === o.columnName);
  let columnIsSorted = false;
  let columnSort: SortDirection | undefined = undefined;
  let sortIndex: number | null = null;

  if (orderIndex !== -1) {
    columnIsSorted = true;
    columnSort = orders[orderIndex].ascending ? 'asc' : 'desc';
    sortIndex = orderIndex;
  }

  return { columnIsSorted, columnSort, sortIndex };
}

interface IComputeAgColumnParams {
  column: ViewColumn;
  viewColumnMetadata: ViewColumn;
  columnFormats: { [key: string]: TableColumnFormat };
  dataFormatter?: (params: ICellRendererParams) => React.ReactElement;
  datasetUid: string;
  displayColumnFilters?: boolean;
  domain: string;
  headerFormat: FormatStyle;
  hierarchyColumns?: HierarchyColumnConfig[];
  hierarchyConfig?: Hierarchy;
  nonStandardAggregations?: ColumnAggregation[] | null;
  removedHierarchyColumnNames?: Set<string>;
  showAgGridColumnAggregations?: boolean;
  showAgGridColumnMenu?: boolean;
  useSetFilters?: boolean;
  vifOrderConfig: OrderConfig[];
  showColumnMenuKebab?: boolean;
  mainMenuItems?: (string | MenuItemDef)[];
  columns?: ViewColumn[];
  setIsCustomSortModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  selectedColumns?: ViewColumn[];
  setSelectedColumns?: React.Dispatch<React.SetStateAction<ViewColumn[]>>;
  getUniqueValues?: (
    columns: ViewColumn[],
    order: OrderConfig[],
    customSortConfig: CustomSortConfig | undefined,
  ) => Promise<UniqueValuesData>;
  distinctValuesCountMap?: object;
  columnMetadata?: ViewColumn[];
  getSortIcons: ISortIcons;
  setCurrentSortState?: React.Dispatch<React.SetStateAction<OrderConfig[]>>;
  handleCustomSortCycle?: (newOrderConfig: OrderConfig[], hierarchyId?: string) => void;
}

export default function computeAgColumn({
  column,
  viewColumnMetadata,
  columnFormats,
  datasetUid,
  displayColumnFilters,
  domain,
  headerFormat,
  hierarchyColumns = [],
  hierarchyConfig,
  nonStandardAggregations,
  removedHierarchyColumnNames,
  showAgGridColumnAggregations = false,
  showAgGridColumnMenu = false,
  useSetFilters,
  vifOrderConfig,
  showColumnMenuKebab = false,
  mainMenuItems = [],
  columns,
  selectedColumns,
  setIsCustomSortModalOpen,
  setSelectedColumns,
  getUniqueValues,
  distinctValuesCountMap,
  columnMetadata,
  getSortIcons,
  setCurrentSortState,
  handleCustomSortCycle,
}: IComputeAgColumnParams): ColDef | null {
  const hierarchyConfigIdx = hierarchyColumns.findIndex(({ columnName }) => columnName === column.fieldName);
  const localHierarchyConfig = hierarchyColumns[hierarchyConfigIdx];

  const { columnIsSorted, columnSort, sortIndex } = getColumnSort(column, vifOrderConfig);

  const isColumnGrouping = localHierarchyConfig?.isGrouping ?? false;
  const isColumnPivoting = localHierarchyConfig?.isPivoting ?? false;
  const isColumnHidden = (hierarchyConfig ? localHierarchyConfig?.hidden : column.hide) ?? false;
  const hierarchyAggregation = localHierarchyConfig?.aggregation ?? null;
  const shouldResetRowIndex = removedHierarchyColumnNames?.has(column.fieldName);

  // to ag-grid, `number` will define the ordering of the group columns;
  // `null` means to clear the existing value (but can only be used if there _is_ an existing value)
  // `undefined` means that this isn't a row group (it also means don't change the existing value, if present)
  let rowGroupIndex: number | null | undefined;
  if (shouldResetRowIndex) {
    rowGroupIndex = null;
  } else if (isColumnGrouping) {
    rowGroupIndex = hierarchyConfigIdx;
  }

  let pivotIndex;
  let pivot = false;
  if (isPivotFeatureFlagOn() && isColumnPivoting) {
    pivot = true;
    // Pivot columns are always stored after row group columns in the hierarchy config
    const numberOfRowGroups = hierarchyColumns.filter((col) => col.isGrouping === true).length;
    pivotIndex = hierarchyConfigIdx - numberOfRowGroups;
  }

  const columnFormat = columnFormats?.[column.fieldName];
  const displayName = getAgTableHeaderName(columnFormat, viewColumnMetadata);
  const sortable = !isGeospatial(column.dataTypeName);

  const filterObject = displayColumnFilters
    ? getAgColFilterObject({
        datasetUid,
        columnFieldName: column.fieldName,
        dataTypeName: column.dataTypeName,
        useSetFilters: !!useSetFilters,
      })
    : {};

  // Only include the menuTabs property in the ColDef if requested
  const menuTabObject: { menuTabs?: ColumnMenuTab[] | undefined } = showAgGridColumnMenu
    ? { menuTabs: ['generalMenuTab', 'filterMenuTab'] }
    : {};

  const applicableNonStandardAggregations = (nonStandardAggregations ?? []).filter(
    ({ fieldName }) => fieldName === column.fieldName,
  );

  const getMainMenuItems = (params: GetMainMenuItemsParams) => {
    const colId = params.column.getColId();
    const columnMap = get(distinctValuesCountMap, colId || '');
    const isColumnDisabled = parseInt(columnMap) > MAX_CUSTOM_SORT_UNIQUE_ROW_COUNT;
    return getCustomSortMenuItems({
      getMainMenuParams: params,
      mainMenuItems,
      columns: columnMetadata,
      hierarchies: hierarchyColumns,
      selectedColumns,
      setIsCustomSortModalOpen,
      setSelectedColumns,
      getUniqueValues,
      vifOrderConfig,
      columnFormats,
      hierarchyId: hierarchyConfig?.id,
      customSortConfig: hierarchyConfig?.columnSortConfig,
      isColumnDisabled,
      distinctValuesCountMap,
      setCurrentSortState,
      handleCustomSortCycle,
    });
  };

  const icons = getSortIcons({
    columnName: column.fieldName,
  });

  const wrapHeaders = headerFormat?.wrapHeaders ?? false;

  const colDef: ColDef = {
    ...filterObject,
    ...menuTabObject,
    width: column.width,
    /**
     * `flex` overrides `width`, so it should only be set on columns
     * where the user has not selected a column width.
     */
    flex: column.width ? undefined : 1,
    ...(!isCustomSortEnabled() && {
      // TODO: remove when custom sort flag is enabled
      sort: columnIsSorted ? columnSort : undefined,
      sortIndex,
    }),
    sortable,

    colId: column.fieldName,
    headerName: displayName,
    field: column.fieldName,

    allowedAggFuncs: getAllowedColumnAggregationFunctions(column, applicableNonStandardAggregations),
    aggFunc: hierarchyAggregation,
    headerTooltip: displayName,
    headerClass: headerClassFunction,
    hide: isColumnGrouping || isColumnHidden, // Otherwise AG-grid will show grouped column and original column which is redundant
    rowGroupIndex, // https://ag-grid.com/react-data-grid//column-properties/#reference-grouping-rowGroupIndex
    pivotIndex,
    pivot,

    cellRendererParams: {
      datasetUid,
      domain,
      wrapWithDivElement: true,
      customLabels: hierarchyConfig?.customLabels,
    },
    cellRenderer: ValueRenderer,
    cellStyle: cellStyleFunction,
    tooltipComponentParams: {
      datasetUid,
      domain,
    },
    tooltipValueGetter: tooltipValueGetterFunction,
    valueGetter: valueGetterFunction,
    // this toggles our wrapped text feature
    // which is only applicable using the style
    // options found in Conditional Formatting
    wrapText: columnFormat?.style?.fontStyle?.isWrapped,
    autoHeight: columnFormat?.style?.fontStyle?.isWrapped,
    mainMenuItems: showColumnMenuKebab ? getMainMenuItems : undefined,
    ...(isCustomSortEnabled() && {
      icons: icons,
    }),
    wrapHeaderText: wrapHeaders,
    autoHeaderHeight: wrapHeaders,
  };

  if (showAgGridColumnAggregations) {
    // A column must be unique. It can be either a row group, an aggregation (value) or a pivot.
    const isRowGroup = colDef.rowGroupIndex != null;
    const isValue = colDef.aggFunc != null;
    const isPivot = colDef.pivotIndex != null;
    colDef.enableRowGroup = !isValue && !isPivot ? GROUPABLE_DATA_TYPES.has(column.dataTypeName) : false;
    colDef.enableValue = !isRowGroup && !isPivot;
    colDef.enablePivot = !isRowGroup && !isValue;
    // The default aggregation is 'sum' for number columns and 'count' for all others
    colDef.defaultAggFunc = column.dataTypeName === SoQLType.SoQLNumberT ? 'sum' : 'count';
  }

  return colDef;
}
